export interface OpenRouterModelVariant {
  suffix: `:${string}`;
  label: string;
  description: string;
}

/**
 * Dynamic OpenRouter variants are valid for every base model. Static variants
 * such as :free and :thinking are model-specific and arrive as their own rows
 * in the OpenRouter model catalog, so they deliberately do not live here.
 */
export const OPENROUTER_DYNAMIC_MODEL_VARIANTS: readonly OpenRouterModelVariant[] = [
  {
    suffix: ':nitro',
    label: 'Fastest',
    description: 'Prioritize providers with the highest throughput',
  },
  {
    suffix: ':floor',
    label: 'Lowest price',
    description: 'Prioritize providers with the lowest price',
  },
];

export function modelIdWithVariant(modelId: string, suffix: `:${string}`): string {
  return `${modelId}${suffix}`;
}

export function isBaseOpenRouterModel(modelId: string): boolean {
  const slash = modelId.indexOf('/');
  return slash >= 0 && !modelId.slice(slash + 1).includes(':');
}

export function modelOrDynamicVariantIsSelected(
  modelId: string,
  selectedModels: readonly string[],
): boolean {
  return selectedModels.some(
    (selected) => selected === modelId
      || OPENROUTER_DYNAMIC_MODEL_VARIANTS.some(
        ({ suffix }) => selected === modelIdWithVariant(modelId, suffix),
      ),
  );
}

interface OpenRouterModelVariantTagsProps {
  modelId: string;
  selectedModels: readonly string[];
  disabled?: boolean;
  onToggle: (modelId: string) => void;
}

/** Reusable controls for adding dynamic OpenRouter model variants. */
export function OpenRouterModelVariantTags({
  modelId,
  selectedModels,
  disabled = false,
  onToggle,
}: OpenRouterModelVariantTagsProps) {
  if (!isBaseOpenRouterModel(modelId)) return null;

  return (
    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      {OPENROUTER_DYNAMIC_MODEL_VARIANTS.map(({ suffix, label, description }) => {
        const variantModelId = modelIdWithVariant(modelId, suffix);
        const selected = selectedModels.includes(variantModelId);
        return (
          <button
            key={suffix}
            type="button"
            className={selected ? 'model-variant-tag model-variant-tag-selected' : 'model-variant-tag'}
            aria-pressed={selected}
            aria-label={`${selected ? 'Remove' : 'Add'} ${variantModelId}`}
            title={`${suffix} — ${description}`}
            disabled={disabled}
            onClick={() => onToggle(variantModelId)}
          >
            {suffix} <span className="model-variant-tag-label">{label}</span>
          </button>
        );
      })}
    </span>
  );
}
